import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Input component following UI design specifications
 * @param {Object} props - Component props
 * @param {React.Ref} ref - Component ref
 * @returns {React.ReactElement} Input component
 */
const Input = forwardRef(({
  label,
  placeholder,
  error,
  helperText,
  disabled = false,
  required = false,
  className = '',
  type = 'text',
  size = 'medium',
  variant = 'default',
  startIcon,
  endIcon,
  ...props
}, ref) => {
  // Base styles
  const baseStyles = `
    w-full transition-all duration-200 focus:outline-none 
    placeholder-gray-500 disabled:bg-gray-50 disabled:cursor-not-allowed
  `;

  // Size styles
  const sizeStyles = {
    small: 'px-3 py-2 text-sm',
    medium: 'px-4 py-3 text-sm',
    large: 'px-4 py-4 text-base'
  };

  // Variant styles
  const variantStyles = {
    default: `
      border border-gray-300 bg-white text-gray-900
      focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
      hover:border-gray-400
    `,
    filled: `
      border-0 bg-gray-100 text-gray-900
      focus:ring-2 focus:ring-emerald-500 focus:bg-white
      hover:bg-gray-50
    `,
    outlined: `
      border-2 border-gray-200 bg-transparent text-gray-900
      focus:ring-0 focus:border-emerald-500
      hover:border-gray-300
    `
  };

  // Error styles
  const errorStyles = error ? `
    border-red-300 bg-red-50 text-red-900 placeholder-red-400
    focus:ring-red-500 focus:border-red-500
  ` : '';

  // Border radius
  const borderRadius = 'rounded-xl';

  // Combine input classes
  const inputClasses = [
    baseStyles,
    sizeStyles[size],
    variantStyles[variant],
    borderRadius,
    errorStyles,
    startIcon ? 'pl-10' : '',
    endIcon ? 'pr-10' : '',
    className
  ].join(' ').replace(/\s+/g, ' ').trim();

  return (
    <div className="w-full">
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input container */}
      <div className="relative">
        {/* Start icon */}
        {startIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <div className="h-5 w-5 text-gray-400">
              {startIcon}
            </div>
          </div>
        )}

        {/* Input field */}
        <input
          ref={ref}
          type={type}
          className={inputClasses}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          {...props}
        />

        {/* End icon */}
        {endIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <div className="h-5 w-5 text-gray-400">
              {endIcon}
            </div>
          </div>
        )}
      </div>

      {/* Helper text or error message */}
      {(error || helperText) && (
        <p className={`mt-2 text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>
          {error || helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

Input.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  variant: PropTypes.oneOf(['default', 'filled', 'outlined']),
  startIcon: PropTypes.node,
  endIcon: PropTypes.node
};

export default Input; 